import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Spinner, Modal } from 'react-bootstrap';
import { api } from '../../../shared/api';
import { formatDateTime, formatDate } from '../../../shared/utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

const RentalSettingsGeneral = ({ rental, setRental }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    rental_status: '',
    date_from: '',
    date_to: '',
    rental_date: '',
    note: '',
    rental_reason: ''
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (formattedValues) => api.patch(`rentals/api/rentals/${rental.id}/`, formattedValues),
    onSuccess: (response) => {
      setRental(response);
      setSuccess(true);
      setShowEditModal(false);
      window.scrollTo(0, 0);
    },
    onError: () => setError(t('rentals.settings.general.updateError')),
  });
  const loading = updateMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccess(false);
    setError(null);
    const formattedValues = {
      ...formData,
      date_from: new Date(formData.date_from).toISOString(),
      date_to: new Date(formData.date_to).toISOString(),
      rental_date: formData.rental_date
    };
    updateMutation.mutate(formattedValues);
  };

  // Initialize form with rental data
  useEffect(() => {
    if (rental) {
      // Convert dates to format expected by input type="datetime-local"
      const dateFrom = rental.date_from ? new Date(rental.date_from).toISOString().slice(0, 16) : '';
      const dateTo = rental.date_to ? new Date(rental.date_to).toISOString().slice(0, 16) : '';
      const rentalDate = rental.rental_date ? rental.rental_date : '';

      setFormData({
        rental_status: rental.rental_status || '',
        date_from: dateFrom,
        date_to: dateTo,
        rental_date: rentalDate,
        note: rental.note || '',
        rental_reason: rental.rental_reason || ''
      });
    }
  }, [rental]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

return (
    <>
      {success && (
        <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
          {t('rentals.settings.general.updateSuccess')}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <h2 className="card-title">{t('rentals.settings.general.title')}</h2>
      <Row className="g-3 mb-3">
        <Col md={6}>
          <div className="form-label">{t('rentals.settings.general.status')}</div>
          <input
            type="text"
            className="form-control"
            value={rental.status_display || rental.rental_status}
            readOnly
          />
        </Col>
        <Col md={6}>
          <div className="form-label">{t('rentals.settings.general.customer')}</div>
          <input
            type="text"
            className="form-control"
            value={rental.customer_details?.resp_person || ''}
            readOnly
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <div className="form-label">{t('rentals.settings.general.rentalReason')}</div>
          <input
            type="text"
            className="form-control"
            value={rental.rental_reason_details?.name || ''}
            readOnly
          />
        </Col>
        <Col md={6}>
          <div className="form-label">{t('rentals.settings.general.dates')}</div>
          <input
            type="text"
            className="form-control"
            value={`${formatDate(rental.date_from)} – ${formatDate(rental.date_to)}`}
            readOnly
          />
        </Col>
      </Row>

      {rental.note && (
        <Row className="g-3 mb-3">
          <Col md={12}>
            <div className="form-label">{t('rentals.settings.general.notes')}</div>
            <textarea
              className="form-control"
              rows="3"
              value={rental.note}
              readOnly
            ></textarea>
          </Col>
        </Row>
      )}

      <Row className="mt-4">
        <Col>
          <Button variant="primary" onClick={() => setShowEditModal(true)}>
            {t('rentals.settings.general.edit')}
          </Button>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>{t('rentals.settings.general.edit')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="date_from">
                  <Form.Label>{t('rentals.settings.general.from')}</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="date_from"
                    value={formData.date_from}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="date_to">
                  <Form.Label>{t('rentals.settings.general.to')}</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="date_to"
                    value={formData.date_to}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="note">
              <Form.Label>{t('rentals.settings.general.notes')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="note"
                value={formData.note}
                onChange={handleInputChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => setShowEditModal(false)}
              >
                {t('rentals.settings.general.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('common.saving')}
                  </>
                ) : (
                  t('rentals.settings.general.saveChanges')
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RentalSettingsGeneral;
